"use client";

import {
  type ComponentProps,
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { animated, useSpring } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import { useWindowSize } from "usehooks-ts";

import StoreLocatorContext from "@/context/StoreLocatorContext";
import { useSelector } from "@/redux/hooks";
import { getShowBanner } from "@/redux/selectors/header";

const INERTIAL_THRESHOLD = 0.4;
const MOBILE_BOTTOM_TOUCH_POINT = 100;

// Determine the next snap position based on the current position, direction, and available snap positions
const nextSnap = (current: number, direction: number, positions: number[]) => {
  const candidates =
    direction < 0
      ? positions.filter((p) => p <= current).sort((a, b) => b - a)
      : positions.filter((p) => p >= current).sort((a, b) => a - b);

  if (candidates.length === 0) {
    return nearestSnap(current, positions);
  }

  const nextClosestSnap = candidates[0];
  if (nextClosestSnap === current && candidates.length > 1) {
    return candidates[1];
  }
  return nextClosestSnap;
};

// Find the nearest snap position to the current position
const nearestSnap = (current: number, positions: number[]): number => {
  return positions.reduce((prev, curr) =>
    Math.abs(curr - current) < Math.abs(prev - current) ? curr : prev,
  );
};

// Convert snap position enum to corresponding y value based on available snap positions
const snapPositionToYValue = (
  position: SnapPositions,
  snapPositions: number[],
) => {
  switch (position) {
    case SnapPositions.Full:
      return snapPositions[0];
    case SnapPositions.Minimized:
      return snapPositions[2];
    case SnapPositions.Half:
      return snapPositions[1];
    case SnapPositions.Unknown:
    default:
      return undefined;
  }
};

// Determine the snap position based on the y value and available snap positions
const yValueToSnapPosition = (yValue: number, snapPositions: number[]) => {
  switch (yValue) {
    case snapPositions[0]:
      return SnapPositions.Full;
    case snapPositions[1]:
      return SnapPositions.Half;
    case snapPositions[2]:
      return SnapPositions.Minimized;
    default:
      return SnapPositions.Unknown;
  }
};

export enum SnapPositions {
  Full = "full",
  Half = "half",
  Minimized = "minimized",
  Unknown = "unknown", // for when the position is not yet determined
}

export const DraggableContentContext = createContext<{
  updateScreenPosition: (position: SnapPositions) => void;
  screenPosition: SnapPositions;
}>({
  screenPosition: SnapPositions.Unknown,
  updateScreenPosition: () => {},
});

const DraggableContent = ({
  children,
  ...props
}: ComponentProps<typeof animated.div> & PropsWithChildren) => {
  const { fullDrawerEnabled } = useContext(StoreLocatorContext);
  const { screenPosition, updateScreenPosition } = useContext(
    DraggableContentContext,
  );
  const appBannerDisplayed = useSelector(getShowBanner);

  const { height } = useWindowSize();
  const totalNavHeight = appBannerDisplayed ? 170 : 88;

  // Define snap positions based on available window height
  const snapPositions = useMemo(() => {
    const availableHeight = height - totalNavHeight;
    // divide by 2 to split the screen in half
    // (negative goes up y axis, positive goes down y axis)
    const halfHeight = availableHeight / 2;

    const bounds = {
      bottom: halfHeight - MOBILE_BOTTOM_TOUCH_POINT,
      top: -halfHeight,
    };

    return [
      bounds.top,
      0, // 0 is the middle of the screen
      bounds.bottom,
    ];
  }, [height, totalNavHeight]);

  const [{ y }, api] = useSpring(
    {
      y:
        snapPositionToYValue(
          fullDrawerEnabled ? SnapPositions.Full : screenPosition,
          snapPositions,
        ) ?? snapPositions[1],
    },
    [snapPositions],
  );

  // Function to animate content to a specific y position
  const animateContent = useCallback(
    (yPosition: number) => {
      api.start({
        config: { friction: 26, tension: 170 },
        immediate: false,
        y: yPosition,
      });
    },
    [api],
  );

  // Animate content whenever the current snap position changes
  // (i.e. when updateScreenPosition() is called in a child component)
  useEffect(() => {
    const yPosition = snapPositionToYValue(screenPosition, snapPositions);
    if (yPosition !== undefined) {
      animateContent(yPosition);
    }
  }, [screenPosition, snapPositions, animateContent]);

  const bind = useDrag(
    ({
      last,
      velocity: [_vx, vy],
      direction: [_dx, dy],
      offset: [_ox, oy],
    }) => {
      // if the event is the last drag event
      if (last) {
        // On drag end, determine the final snap position based on velocity(vy) and direction(dy)
        const final =
          Math.abs(vy) > INERTIAL_THRESHOLD
            ? nextSnap(oy, dy < 0 ? -1 : 1, snapPositions)
            : nearestSnap(oy, snapPositions);

        const finalSnapPosition = yValueToSnapPosition(final, snapPositions);

        // if the final snap position is the same as the current one,
        // we need to still animate to the same snap position
        // or else the content will stay in the middle of the screen
        if (finalSnapPosition === screenPosition) {
          animateContent(final);
        } else {
          updateScreenPosition(finalSnapPosition);
        }
      } else {
        // During dragging, update the content position
        api.start({ immediate: true, y: oy });
      }
    },
    {
      bounds: { bottom: snapPositions[2], top: snapPositions[0] },
      from: () => [0, y.get()],
    },
  );

  return (
    <animated.div
      data-testid="draggable-content"
      className="bg-white touch-none absolute z-10 top-1/2 right-0 left-0 flex flex-col overflow-y-hidden"
      style={{
        // heights needed to ensure window is filled but not exceeded
        height: `calc(100vh - ${totalNavHeight}px)`,
        maxHeight: `calc(100vh - ${totalNavHeight}px)`,
        transform: y.to((val) => `translateY(${val}px)`),
      }}
      {...bind()}
      {...props}
    >
      <div
        className="pt-[10px] md:hidden flex justify-center items-center bg-white"
        data-testid="listing-wrapper-handle"
      >
        <span className="bg-light-grey rounded-full w-[64px] h-[6px]" />
      </div>
      {children}
    </animated.div>
  );
};

export default ({
  initialPosition = SnapPositions.Half,
  ...props
}: ComponentProps<typeof DraggableContent> & {
  initialPosition?: SnapPositions;
}) => {
  const [screenPosition, updateScreenPosition] = useState(initialPosition);
  return (
    <DraggableContentContext.Provider
      value={{ screenPosition, updateScreenPosition }}
    >
      <DraggableContent {...props} />
    </DraggableContentContext.Provider>
  );
};
